/**
 * @fileoverview Formulário de Descontos de Negociação (KPD, DLOC, DLOM)
 * 
 * @module DiscountsAssumptionsForm
 * @description
 * Permite editar descontos aplicados ao Equity Value para chegar ao Valor Justo:
 * - KPD (Key Person Discount): Dependência de pessoa-chave
 * - DLOC (Discount for Lack of Control): Falta de controle
 * - DLOM (Discount for Lack of Marketability): Falta de liquidez
 * 
 * @features
 * - Edição de cada desconto individual
 * - Cálculo do desconto combinado
 * - Explicação de cada tipo de desconto
 * - Validação de ranges típicos
 * - Impacto visual no valuation
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Button,
  Divider,
  LinearProgress,
  Chip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme,
} from '@mui/material';
import { IconInfo, IconAlertTriangle, IconTrendingDown, IconUser, IconTarget, IconDroplet, IconDashboard } from '../../utils/icons';
import { Info, AlertTriangle, CheckCircle } from 'lucide-react';

import { PercentageInput } from './CommonInputs';

/**
 * Interface para descontos de negociação
 */
export interface DiscountsAssumptions {
  kpd_mode: 'in_wacc' | 'as_haircut'; // Modo de aplicação do KPD
  kpd: number; // Key Person Discount (0-30%)
  dloc: number; // Discount for Lack of Control (0-25%)
  dlom: number; // Discount for Lack of Marketability (0-35%)
}

/**
 * Valida se os descontos estão dentro de limites aceitáveis
 * @param discounts Descontos a validar
 * @returns true se descontos são válidos (<= 50%)
 */
export const validateDiscounts = (discounts: DiscountsAssumptions): boolean => {
  // Calcular desconto combinado baseado no modo KPD
  const descontoCombinado = discounts.kpd_mode === 'in_wacc'
    ? 1 - ((1 - 0) * (1 - discounts.dloc) * (1 - discounts.dlom))  // KPD no WACC
    : 1 - ((1 - discounts.kpd) * (1 - discounts.dloc) * (1 - discounts.dlom)); // KPD como haircut
  return descontoCombinado <= 0.50; // ERRO 6: Bloqueio acima de 50%
};

/**
 * Props do componente
 */
interface DiscountsAssumptionsFormProps {
  discounts: DiscountsAssumptions;
  onChange: (discounts: DiscountsAssumptions) => void;
  
  // Valores para demonstração de impacto (opcional)
  equityValue?: number;
}

/**
 * Defaults baseados no modelo Excel
 */
const DEFAULTS: DiscountsAssumptions = {
  kpd_mode: 'in_wacc', // Modo recomendado (evita dupla contagem)
  kpd: 0.15, // 15% - dependência significativa de sócios
  dloc: 0.10, // 10% - participação minoritária
  dlom: 0.27, // 27% - empresa fechada sem liquidez
};

/**
 * Limites de validação (ranges típicos de mercado)
 * ✅ CORRIGIDO: Limites agora em escala percentual (0-100) para compatibilidade com PercentageInput
 */
const LIMITS = {
  kpd: { min: 0, max: 40 }, // 0-40%
  dloc: { min: 0, max: 30 }, // 0-30%
  dlom: { min: 0, max: 45 }, // 0-45%
};

/**
 * Componente Principal
 */
export const DiscountsAssumptionsForm: React.FC<DiscountsAssumptionsFormProps> = ({
  discounts,
  onChange,
  equityValue = 23803419, // Exemplo do modelo Excel
}) => {
  const theme = useTheme();
  
  const handleChange = (field: keyof DiscountsAssumptions, value: number | string) => {
    onChange({
      ...discounts,
      [field]: value,
    });
  };

  const handleReset = () => {
    onChange(DEFAULTS);
  };

  // ========== CÁLCULOS ==========
  
  // Desconto Combinado - condicional baseado no modo KPD
  const descontoCombinado = discounts.kpd_mode === 'in_wacc'
    ? 1 - ((1 - 0) * (1 - discounts.dloc) * (1 - discounts.dlom))  // KPD no WACC (não entra aqui)
    : 1 - ((1 - discounts.kpd) * (1 - discounts.dloc) * (1 - discounts.dlom)); // KPD como haircut
  
  // Valor Justo = Equity Value × (1 - Desconto Combinado)
  const valorJusto = equityValue * (1 - descontoCombinado);
  const reducao = equityValue - valorJusto;

  // Classificação do desconto total
  const classificarDesconto = () => {
    if (descontoCombinado < 0.20) return { label: 'Conservador', color: 'success' as const };
    if (descontoCombinado < 0.40) return { label: 'Moderado', color: 'warning' as const };
    if (descontoCombinado < 0.60) return { label: 'Agressivo', color: 'error' as const };
    return { label: 'Muito Agressivo', color: 'error' as const };
  };

  const classificacao = classificarDesconto();

  // ========== VALIDAÇÃO CRÍTICA ==========
  const hasBlockingError = descontoCombinado > 0.50; // ERRO 6: Bloquear >50%
  const hasWarning = descontoCombinado > 0.40 && descontoCombinado <= 0.50;
  const isConservative = descontoCombinado <= 0.35; // Dentro do range de mercado

  return (
    <Paper sx={{ p: 3, border: hasBlockingError ? '2px solid' : 'none', borderColor: 'error.main' }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconTrendingDown size={24} />
          Descontos de Negociação
        </Typography>
        {hasBlockingError && (
          <Chip label="BLOQUEADO" color="error" size="small" />
        )}
        {hasWarning && !hasBlockingError && (
          <Chip label="ATENÇÃO" color="warning" size="small" />
        )}
      </Box>

      <Alert severity="info" icon={<IconInfo size={20} />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          Descontos aplicados para refletir riscos e limitações específicas da transação. 
          O KPD pode ser aplicado de duas formas: <strong>no WACC</strong> (aumenta o custo de capital, reduzindo o EV) 
          ou <strong>como desconto direto</strong> no Equity Value (junto com DLOC e DLOM).
        </Typography>
      </Alert>

      {/* MODO DE APLICAÇÃO DO KPD */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconDashboard size={20} />
          Modo de Aplicação do KPD
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Escolha como o Key Person Discount será aplicado no valuation
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={discounts.kpd_mode}
            onChange={(e) => handleChange('kpd_mode', e.target.value)}
          >
            <FormControlLabel 
              value="in_wacc" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Embutido no WACC (recomendado)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Aumenta o custo de capital, reduzindo o EV desde o início. 
                    Evita dupla contagem e é mais preciso tecnicamente.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel 
              value="as_haircut" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Como Desconto Direto no Equity Value
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Aplicado após calcular o EV, junto com DLOC e DLOM. 
                    Mais simples, mas pode levar a dupla contagem se não for cuidadoso.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Alert severity={discounts.kpd_mode === 'in_wacc' ? 'success' : 'info'} sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Modo atual:</strong> {discounts.kpd_mode === 'in_wacc' ? 'KPD no WACC' : 'KPD como desconto direto'}
            {discounts.kpd_mode === 'in_wacc' && (
              <> — O desconto total abaixo <strong>não inclui</strong> o KPD (ele já está embutido no WACC).</>
            )}
            {discounts.kpd_mode === 'as_haircut' && (
              <> — O desconto total abaixo <strong>inclui</strong> o KPD de {(discounts.kpd * 100).toFixed(0)}%.</>
            )}
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* KPD - Key Person Discount */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconUser size={20} />
          KPD - Key Person Discount
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Desconto pela dependência de pessoas-chave (sócios, fundadores, executivos críticos)
        </Typography>

        <PercentageInput
          label="KPD"
          value={discounts.kpd}
          onChange={(valor) => handleChange('kpd', valor)}
          min={LIMITS.kpd.min}
          max={LIMITS.kpd.max}
          helpText="Típico: 10-25% para empresas dependentes de fundadores"
          disabled={discounts.kpd_mode === 'in_wacc'}
        />

        {discounts.kpd_mode === 'in_wacc' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Nota:</strong> KPD está embutido no WACC. O campo acima é apenas informativo. 
              Para aplicar KPD como desconto direto, altere o modo acima.
            </Typography>
          </Alert>
        )}

        {discounts.kpd_mode === 'as_haircut' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Contexto:</strong> Sétima e 555 Studios têm forte dependência dos sócios fundadores 
              para relacionamentos comerciais e gestão estratégica. Desconto de {(discounts.kpd * 100).toFixed(0)}% 
              reflete este risco de continuidade.
            </Typography>
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* DLOC - Discount for Lack of Control */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconTarget size={20} />
          DLOC - Discount for Lack of Control
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Desconto por participação minoritária sem poder de decisão estratégica
        </Typography>

        <PercentageInput
          label="DLOC"
          value={discounts.dloc}
          onChange={(valor) => handleChange('dloc', valor)}
          min={LIMITS.dloc.min}
          max={LIMITS.dloc.max}
          helpText="Típico: 10-20% para participações < 50%"
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Explicação:</strong> Investidores minoritários não podem influenciar decisões como 
            distribuição de lucros, entrada/saída de sócios, ou estratégia de crescimento. Este desconto 
            compensa a falta de controle gerencial.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* DLOM - Discount for Lack of Marketability */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconDroplet size={20} />
          DLOM - Discount for Lack of Marketability
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Desconto pela impossibilidade de vender rapidamente a participação (empresa fechada)
        </Typography>

        <PercentageInput
          label="DLOM"
          value={discounts.dlom}
          onChange={(valor) => handleChange('dlom', valor)}
          min={LIMITS.dlom.min}
          max={LIMITS.dlom.max}
          helpText="Típico: 15-35% para empresas fechadas sem liquidez"
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Explicação:</strong> Diferente de ações de empresas abertas (que podem ser vendidas 
            instantaneamente na bolsa), participações em empresas fechadas têm baixa liquidez e custos 
            de transação elevados. DLOM compensa essa iliquidez.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Desconto Combinado e Impacto */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconTrendingDown size={20} /> Desconto Total Combinado
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {discounts.kpd_mode === 'in_wacc' 
            ? 'Fórmula: 1 - (1 - DLOC) × (1 - DLOM)   [KPD no WACC]'
            : 'Fórmula: 1 - (1 - KPD) × (1 - DLOC) × (1 - DLOM)'}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255, 183, 77, 0.08)'  // Laranja translúcido no dark
                : 'rgba(255, 152, 0, 0.08)',  // Laranja translúcido no light
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 183, 77, 0.3)'
                : 'rgba(255, 152, 0, 0.3)',
            }}>
              <Typography 
                variant="body2" 
                gutterBottom
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                Desconto Total Combinado
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography 
                  variant="h4"
                  sx={{
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(255, 183, 77, 1)'
                      : 'rgba(255, 152, 0, 1)',
                  }}
                >
                  {(descontoCombinado * 100).toFixed(1)}%
                </Typography>
                <Chip label={classificacao.label} color={classificacao.color} size="small" />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={descontoCombinado * 100} 
                color="warning"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  mt: 1, 
                  display: 'block',
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {discounts.kpd_mode === 'in_wacc'
                  ? `= 1 - (${((1 - discounts.dloc) * 100).toFixed(0)}% × ${((1 - discounts.dlom) * 100).toFixed(0)}%)`
                  : `= 1 - (${((1 - discounts.kpd) * 100).toFixed(0)}% × ${((1 - discounts.dloc) * 100).toFixed(0)}% × ${((1 - discounts.dlom) * 100).toFixed(0)}%)`
                }
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(244, 67, 54, 0.08)'  // Vermelho translúcido no dark
                : 'rgba(211, 47, 47, 0.08)', // Vermelho translúcido no light
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(244, 67, 54, 0.3)'
                : 'rgba(211, 47, 47, 0.3)',
            }}>
              <Typography 
                variant="body2" 
                gutterBottom
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                Redução no Valuation
              </Typography>
              <Typography 
                variant="h4" 
                gutterBottom
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(244, 67, 54, 1)'
                    : 'rgba(211, 47, 47, 1)',
                }}
              >
                {new Intl.NumberFormat('pt-BR', { 
                  style: 'currency', 
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(reducao)}
              </Typography>
              <Typography 
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                De {new Intl.NumberFormat('pt-BR', { 
                  style: 'currency', 
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(equityValue)} para {new Intl.NumberFormat('pt-BR', { 
                  style: 'currency', 
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(valorJusto)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* ERRO 6: Validação aprimorada com bloqueio >50% e benchmarks de mercado */}
        {hasBlockingError && (
          <Alert severity="error" icon={<IconAlertTriangle size={20} />} sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              🚫 VALIDAÇÃO BLOQUEADA
            </Typography>
            <Typography variant="body2">
              Desconto total de <strong>{(descontoCombinado * 100).toFixed(1)}%</strong> é <strong>inviabilizante</strong>.
              Descontos acima de 50% raramente refletem condições reais de mercado e podem invalidar a análise.
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Info size={14} />
              <strong>Benchmark de mercado:</strong> Descontos típicos variam entre 25-35%. 
              Máximo recomendado: 45% em casos excepcionais com riscos extremos devidamente justificados.
            </Typography>
          </Alert>
        )}

        {hasWarning && !hasBlockingError && (
          <Alert severity="warning" icon={<AlertTriangle size={20} />} sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AlertTriangle size={16} />
              <strong>Atenção:</strong> Desconto total de <strong>{(descontoCombinado * 100).toFixed(1)}%</strong> está{' '}
              <strong>acima da média de mercado</strong> (25-35%).
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Certifique-se de que os riscos (dependência de pessoas-chave, falta de controle, iliquidez) 
              justificam este nível de desconto. Considere revisar os percentuais individuais.
            </Typography>
          </Alert>
        )}

        {isConservative && !hasWarning && !hasBlockingError && (
          <Alert severity="success" icon={<CheckCircle size={20} />} sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle size={16} />
              Desconto total de <strong>{(descontoCombinado * 100).toFixed(1)}%</strong> está{' '}
              <strong>dentro do range aceitável</strong> de mercado (25-35%).
            </Typography>
          </Alert>
        )}

        {!isConservative && !hasWarning && !hasBlockingError && (
          <Alert severity="info" icon={<IconInfo size={20} />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              Desconto total de <strong>{(descontoCombinado * 100).toFixed(1)}%</strong> está{' '}
              próximo ao limite superior do range de mercado. Revise se reflete adequadamente os riscos.
            </Typography>
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Breakdown Detalhado */}
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconDashboard size={18} />
          Breakdown dos Descontos
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">
                KPD {discounts.kpd_mode === 'in_wacc' && '(no WACC)'}
              </Typography>
              <Typography variant="h6" color={discounts.kpd_mode === 'in_wacc' ? 'text.disabled' : 'error'}>
                {(discounts.kpd * 100).toFixed(1)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={discounts.kpd * 100} 
                color={discounts.kpd_mode === 'in_wacc' ? 'inherit' : 'error'}
                sx={{ mt: 1, opacity: discounts.kpd_mode === 'in_wacc' ? 0.3 : 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">DLOC</Typography>
              <Typography variant="h6" color="warning">{(discounts.dloc * 100).toFixed(1)}%</Typography>
              <LinearProgress 
                variant="determinate" 
                value={discounts.dloc * 100} 
                color="warning"
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">DLOM</Typography>
              <Typography variant="h6" color="info">{(discounts.dlom * 100).toFixed(1)}%</Typography>
              <LinearProgress 
                variant="determinate" 
                value={discounts.dlom * 100} 
                color="info"
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box display="flex" gap={2} mt={3}>
        <Button variant="outlined" onClick={handleReset}>
          Restaurar Padrões
        </Button>
      </Box>
    </Paper>
  );
};

export default DiscountsAssumptionsForm;
